import {
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  UrlSerializer,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { environment } from '@awo-care/shared-auth';

import { provideHttpClient, withFetch } from '@angular/common/http';
import { GOOGLE_MAPS_API_KEY } from '@solidgroup-agency/ui-dynamic-form';
import {
  MatDateFormats,
  MAT_DATE_FORMATS,
  DateAdapter,
} from '@angular/material/core';
import {
  CustomGermanDateAdapter,
  provideCrossDomainCookieConsent,
} from '@awo-care/utils';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideFirebase,
  USE_EMULATORS,
  FirebaseAnalyticsHandler,
} from '@awo-care/shared-firebase';
import { ANALYTICS_HANDLER } from '@solidgroup-agency/ui-shared-components';
import { WebsiteErrorHandlerService } from './core/services/website-error-handler.service';
import { SeoTitleStrategy } from './core/services/seo-title.strategy';
import { TrailingSlashUrlSerializer } from './core/services/trailing-slash-url-serializer';
import { LanguageService } from '@awo-care/shared-core';

const DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Custom handler as the main ErrorHandler
    {
      provide: ErrorHandler,
      useClass: WebsiteErrorHandlerService,
    },
    // Add the official global listener
    // This connects the browser's global errors to the ErrorHandler
    provideBrowserGlobalErrorListeners(),

    LanguageService, // Language routing for website

    provideHttpClient(withFetch()),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
    ),
    // Sets per-route title, description, canonical and hreflang tags
    { provide: TitleStrategy, useExisting: SeoTitleStrategy },
    // routerLink hrefs end with "/" like the hosted pages — no 301 hop (#1308)
    { provide: UrlSerializer, useClass: TrailingSlashUrlSerializer },

    { provide: DateAdapter, useClass: CustomGermanDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    { provide: GOOGLE_MAPS_API_KEY, useValue: environment.googleMapsAPIKey },

    // Firebase configuration
    ...provideFirebase(
      {
        ...environment.firebaseConfig,
        appId: environment.appIds.website,
        measurementId: environment.measurementIds.website,
      },
      {
        useEmulators: environment.useEmulators,
        useFunctionsEmulatorOnly: environment.useFunctionsEmulatorOnly,
      },
      {
        enableAnalytics:
          environment.production && !!environment.measurementIds.website,
        enableAppCheck: environment.enableAppCheck,
        enablePerformance: environment.enablePerformance,
        performanceRequiresConsent: true,
        recaptchaSiteKey: environment.recaptchaSiteKey,
        tier: environment.tier,
        isTokenAutoRefreshEnabled: true,
      },
    ),
    // Provide emulator flag for services to inject
    { provide: USE_EMULATORS, useValue: environment.useEmulators },
    { provide: ANALYTICS_HANDLER, useClass: FirebaseAnalyticsHandler },
    provideCrossDomainCookieConsent(),
  ],
};
